import React, { useState, useEffect } from 'react';
import { Search, Layers } from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import api from '../../utils/api';

const typeChip = {
  storage: 'chip-blue',
  quarantine: 'chip-red',
  oversize: 'chip-amber',
  unassigned: 'chip-gray',
};

const RackView = () => {
  const [racks, setRacks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    api.get('/rack')
      .then((r) => setRacks(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filterItems = (items) =>
    (items || []).filter((i) => !search || i.itemName.toLowerCase().includes(search.toLowerCase()));

  const rackHasMatch = (rack) =>
    Object.values(rack.floors || {}).some((items) => filterItems(items).length > 0);

  const visibleRacks = search ? racks.filter(rackHasMatch) : racks;

  return (
    <div className="space-y-5">
      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zip-muted" />
        <input
          type="text"
          className="zip-input pl-9"
          placeholder="Search items…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Rack grid */}
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="zip-card h-64 skeleton" />
          ))}
        </div>
      ) : visibleRacks.length === 0 ? (
        <div className="zip-card p-10 text-center text-zip-muted text-sm">
          {search ? 'No items match your search.' : 'No racks or stock found.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {visibleRacks.map((rack) => {
            const accent = rack.rackId === null ? '#64748B' : '#E8192C';
            const floorLabels = Object.keys(rack.floors || {});
            const totalItems = floorLabels.reduce((sum, fl) => sum + filterItems(rack.floors[fl]).length, 0);

            return (
              <div
                key={rack.rackId ?? 'unassigned'}
                className="zip-card overflow-hidden"
                style={{ borderColor: `${accent}33` }}
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between px-5 py-3 border-b"
                  style={{ borderColor: `${accent}33`, background: `${accent}0d` }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: accent }} />
                    <span className="font-bold text-zip-text tracking-tight truncate">{rack.name}</span>
                    <span className="text-[11px] font-mono text-zip-muted flex-shrink-0">{rack.code}</span>
                    <span className={`${typeChip[rack.type] || 'chip-gray'} flex-shrink-0`}>{rack.type}</span>
                  </div>
                  <span
                    className="text-xs font-mono px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: `${accent}20`, color: accent }}
                  >
                    {totalItems} items
                  </span>
                </div>

                {/* Floors */}
                <div className="p-3 space-y-3 max-h-[65vh] overflow-y-auto">
                  {floorLabels.length === 0 ? (
                    <p className="text-center text-sm text-zip-muted py-10">No floors configured</p>
                  ) : (
                    floorLabels.map((floorLabel) => {
                      const floorItems = filterItems(rack.floors[floorLabel]);
                      if (search && floorItems.length === 0) return null;
                      return (
                        <div key={floorLabel}>
                          <div className="flex items-center gap-1.5 px-1 mb-1.5">
                            <Layers size={11} className="text-zip-muted" />
                            <span className="text-[11px] font-mono font-semibold uppercase tracking-wide text-zip-muted">
                              {floorLabel}
                            </span>
                          </div>
                          {floorItems.length === 0 ? (
                            <p className="text-xs text-zip-muted/70 pl-1 pb-2">Empty</p>
                          ) : (
                            <div className="space-y-2">
                              {floorItems.map((item, i) => (
                                <motion.div
                                  key={item._id}
                                  initial={{ opacity: 0, y: 4 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: i * 0.03, duration: 0.18 }}
                                  className={`bg-zip-surface rounded-xl border border-zip-border p-3.5 transition-opacity
                                    ${item.quantity === 0 ? 'opacity-40' : ''}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="font-medium text-sm text-zip-text leading-tight">{item.itemName}</span>
                                    {item.quantity === 0 && (
                                      <span className="chip-gray text-[10px] flex-shrink-0">Out of Stock</span>
                                    )}
                                  </div>
                                  <div className="mt-1.5 flex items-baseline gap-1">
                                    <span className="text-2xl font-bold text-zip-text font-mono">{item.quantity}</span>
                                    <span className="text-xs text-zip-muted">{item.unit}</span>
                                  </div>
                                  <div className="text-[11px] text-zip-muted mt-1 font-mono">
                                    {item.inwardReceipt?.invoiceNumber}
                                    {item.receivedAt ? ` · ${format(new Date(item.receivedAt), 'dd MMM')}` : ''}
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RackView;
